// Składanie promptu do generatora obrazów (SPEC §21.6, §23.24, §23.26). Dwa rodzaje obrazków: obrazek HISTORII na kroki 1–2 (jeden na lekcję, rysowany ze zdań
// KROKU 2 — wzbogaconych, każdy przymiotnik ma być widać) i obrazek SŁOWA (kotwica pamięciowa pytania kroku 4: jedna rzecz, osoba albo czynność na białym tle).
// Scenki, luk, układania zdań i „Co powiesz?” nie ilustrujemy (patch-45).
// Styl rysunku (STYLE) pisze właściciel w art/style.md; tu jest tylko szkielet promptu. Czysty kod — pliki czyta scripts/images-generate.ts.

import { CHARACTERS, lookOf, type Cast, type Character } from './characters.ts';
import type { LessonTarget, LessonV2 } from './levels.ts';

/** Styl zapasowy, gdy nie ma art/style.md — opisany na podstawie wzorcowego rysunku (patyczkowe postacie, brązowy marker, różowe policzki, biel). */
export const DEFAULT_STYLE =
  'Hand-drawn illustration in the style of the reference picture: simple stick-figure people with round white heads, tiny dot eyes, a small smile and rosy pink cheeks; confident dark-brown marker outlines with slightly wobbly lines; hair drawn with hatched brown marker strokes; thin stick limbs without finger detail; soft flat colour fills with a little pencil texture; a pale lavender ground shadow under the characters; a few small yellow sparkles; clean off-white background with plenty of empty space; warm, cheerful, calm mood.';

/** Treść pliku art/style.md → blok STYLE: pomija linie zaczynające się od „#” i puste; null, gdy nic nie zostało. */
export function parseStyleFile(text: string): string | null {
  const body = text.split('\n').filter((l) => !l.trim().startsWith('#')).join(' ').replace(/\s+/g, ' ').trim();
  return body || null;
}

/**
 * Kompozycja (SPEC §23.15, po pierwszych obrazkach testowych 17.10): „far away on the right” dawało dwie osobne grupy, malutkie postacie
 * unoszące się w pustce i luki; nie wskazany wprost wskazujący był losowy. Reguła dotyczy KAŻDEGO obrazka, a linter pilnuje opisów lekcji.
 */
export const COMPOSITION_RULES =
  'COMPOSITION: one compact group — all people stand close together on the same ground line, overlapping a little, with no big empty gaps; people in the background stand just behind the others and are only slightly smaller — never tiny, never far away, never in separate groups; the whole group fills the centre of the picture. Only the person described as pointing points; everybody else keeps their hands relaxed or waves.';

export const DEFAULT_CONSTRAINTS =
  `No text, letters, numbers, logos or watermarks anywhere in the picture. Show exactly the people and objects described and no extra people. Friendly and calm, suitable for all ages (children and adults). Landscape format, main subject centred with a margin around it. ${COMPOSITION_RULES}`;

export const CHARACTER_CONSTRAINTS =
  'One character only, full body, standing and facing the viewer in a neutral friendly pose, plain off-white background, no props, no other people. No text, letters, numbers, logos or watermarks. Suitable for all ages (children and adults).';

export interface PromptOptions {
  style?: string;
  constraints?: string;
  characters?: readonly Character[];
}

/** Rodzaje obrazków lekcji: historia kroków 1–2 (`describe`, plik `<lekcja>-a`) i obrazek słowa (`word`, plik `w-<słowo>`). */
export type ImageKind = 'describe' | 'word';

/** Id pliku obrazka historii lekcji (`describe.image`, np. `a1-001-a`). */
export const imageIdOf = (lesson: LessonV2): string => lesson.describe.image;

/** Id pliku obrazka słowa: jedno słowo = jeden obrazek w całym kursie (cele się nie powtarzają), np. `w-apple`, `w-ice-cream`. */
export const wordImageId = (word: string): string => `w-${word.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;

/** Bohaterowie z biblii postaci wymienieni z imienia w tekście. */
export const charactersIn = (text: string, all: readonly Character[] = CHARACTERS): Character[] => all.filter((c) => new RegExp(`\\b${c.name}\\b`).test(text));

/** Bohaterowie z biblii postaci, o których mówi obrazek kroków 1–2 (imię w zdaniach lub w opisie ujęcia) — np. „Tom has his breakfast”. */
export function describeCharacters(lesson: LessonV2, all: readonly Character[] = CHARACTERS): Character[] {
  const text = [...lesson.describe.lines.map((l) => l.text), ...lesson.enrich.lines.map((l) => l.text), lesson.describe.imagePrompt].join(' ');
  return charactersIn(text, all);
}

const bible = (who: readonly Character[], cast: Cast): string => `CHARACTERS (identical look in every picture): ${who.map((c) => `${c.name} is ${lookOf(c, cast)}`).join('; ')}.`;

/** Skład obrazków lekcji: dorośli, dopóki lekcja nie zaznaczy dzieci (`scene.cast`; pole zostało w scenie, bo opisuje bohaterów całej lekcji). */
export const castOf = (lesson: LessonV2): Cast => lesson.scene.cast ?? 'adult';

export function buildImagePrompt(lesson: LessonV2, opts: PromptOptions = {}): string {
  const style = (opts.style ?? DEFAULT_STYLE).trim();
  const constraints = (opts.constraints ?? DEFAULT_CONSTRAINTS).trim();
  const parts: string[] = [`STYLE: ${style}`];
  const who = describeCharacters(lesson, opts.characters);
  if (who.length > 0) parts.push(bible(who, castOf(lesson)));
  const story = lesson.enrich.lines.map((l) => l.text).join(' ');
  parts.push(`STORY TO ILLUSTRATE (draw every adjective and detail so it can be seen): “${story}”`);
  parts.push(`STAGING: ${lesson.describe.imagePrompt.trim()}`);
  parts.push(`CONSTRAINTS: ${constraints}`);
  return parts.join('\n\n');
}

/**
 * Obrazek słowa (SPEC §23.24): jeden przedmiot, osoba albo czynność, wielki, na gładkim tle — bez postaci z biblii i bez sceny, żeby dziecko
 * rozpoznało SŁOWO, a nie historię. Samego słowa w prompcie nie ma (generator mógłby je napisać na obrazku); jest tylko opis tego, co ma być widać.
 */
export const WORD_CONSTRAINTS =
  'ONE single subject, drawn large and centred with a margin around it, on a plain off-white background with nothing else in the picture, so that a child recognises at a glance what it shows. No text, letters, numbers, logos or watermarks anywhere. Friendly and calm, suitable for all ages (children and adults). Landscape format.';

export function buildWordPrompt(target: Pick<LessonTarget, 'anchor'>, opts: Pick<PromptOptions, 'style' | 'constraints'> = {}): string {
  const draw = target.anchor?.draw?.trim();
  if (!draw) throw new Error('buildWordPrompt: cel bez anchor.draw (kafelek z emoji nie ma obrazka)');
  const style = (opts.style ?? DEFAULT_STYLE).trim();
  return [`STYLE: ${style}`, `SUBJECT: ${draw}`, `CONSTRAINTS: ${(opts.constraints ?? WORD_CONSTRAINTS).trim()}`].join('\n\n');
}

/** Arkusz postaci (wzór wyglądu do kolejnych obrazków): jedna postać, cała sylwetka, białe tło. */
export function buildCharacterPrompt(character: Character, opts: Pick<PromptOptions, 'style'> & { cast?: Cast } = {}): string {
  const style = (opts.style ?? DEFAULT_STYLE).trim();
  return [`STYLE: ${style}`, `CHARACTER SHEET: ${character.name} is ${lookOf(character, opts.cast)}. Draw ${character.name} exactly as described; this picture is the model for every later picture, so keep the design simple and easy to repeat.`, `CONSTRAINTS: ${CHARACTER_CONSTRAINTS}`].join('\n\n');
}

export interface ReferenceImage {
  /** „style” albo imię postaci. */
  role: string;
}

/**
 * Dopisuje do promptu objaśnienie załączonych obrazków referencyjnych (kolejność = kolejność plików w zapytaniu „edits”):
 * pierwszy to wzór stylu, kolejne to arkusze postaci.
 */
export function withReferences(prompt: string, refs: readonly ReferenceImage[]): string {
  if (refs.length === 0) return prompt;
  const lines = refs.map((r, i) => (r.role === 'style' ? `Image ${i + 1} is the STYLE reference: copy its line work, colours, proportions and mood, but not its content.` : `Image ${i + 1} is the CHARACTER reference for ${r.role}: keep the face, hair, clothes and proportions identical.`));
  return `${prompt}\n\nREFERENCE IMAGES: ${lines.join(' ')}`;
}
